import { useRef, useState, useCallback, useEffect } from 'react';

/**
 * Type for the resolver stored in the ref
 */
interface Resolver {
  resolve: () => void;
  reject: (error: Error) => void;
}

/**
 * Configuration for async mutation wrapper
 */
interface UseAsyncMutationConfig {
  /**
   * Called on successful mutation completion.
   * Will be invoked BEFORE the promise resolves.
   */
  onSuccess?: () => void;

  /**
   * Called on mutation error.
   * Will be invoked BEFORE the promise rejects.
   */
  onError?: (error: Error) => void;
}

/**
 * Return type for useAsyncMutation hook
 */
interface UseAsyncMutationResult<TParams> {
  /**
   * Execute the mutation and return a promise
   * If a previous mutation is still pending, it will be rejected first
   */
  mutateAsync: (params: TParams) => Promise<void>;

  /**
   * Whether a mutation is currently in progress
   */
  isLoading: boolean;

  /**
   * Call this from the wrapped mutation's success callback
   */
  onMutationSuccess: () => void;

  /**
   * Call this from the wrapped mutation's error callback
   */
  onMutationError: (error: Error) => void;
}

// Safety timeout to prevent promises from hanging indefinitely (30 seconds)
const MUTATION_TIMEOUT_MS = 30_000;

export function useAsyncMutation<TParams>(
  config: UseAsyncMutationConfig = {}
): UseAsyncMutationResult<TParams> {
  const resolverRef = useRef<Resolver | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { onSuccess, onError } = config;

  // Reject pending promise on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (resolverRef.current) {
        resolverRef.current.reject(new Error('Component unmounted'));
        resolverRef.current = null;
      }
    };
  }, []);

  const mutateAsync = useCallback((_params: TParams): Promise<void> => {
    // If there's a pending promise, reject it to prevent stale resolution
    if (resolverRef.current) {
      resolverRef.current.reject(new Error('Cancelled by new request'));
      resolverRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    return new Promise((resolve, reject) => {
      resolverRef.current = { resolve, reject };
      setIsLoading(true);

      // Safety timeout: reject if the mutation callback is never invoked
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        if (resolverRef.current) {
          setIsLoading(false);
          resolverRef.current.reject(new Error('Mutation timed out'));
          resolverRef.current = null;
        }
      }, MUTATION_TIMEOUT_MS);
    });
  }, []);

  /**
   * Call this from the wrapped mutation's success callback
   */
  const onMutationSuccess = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsLoading(false);
    onSuccess?.();
    resolverRef.current?.resolve();
    resolverRef.current = null;
  }, [onSuccess]);

  /**
   * Call this from the wrapped mutation's error callback
   */
  const onMutationError = useCallback(
    (error: Error) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsLoading(false);
      onError?.(error);
      resolverRef.current?.reject(error);
      resolverRef.current = null;
    },
    [onError]
  );

  return {
    mutateAsync,
    isLoading,
    onMutationSuccess,
    onMutationError,
  };
}
